import {
  PanelLeft,
  Home,
  FileText,
  Network,
  ShoppingBag,
  ShoppingCart,
  Users,
  UserPlus,
  Info,
  Clock,
} from "lucide-react";
import { useRef, useState, useEffect, useContext } from "react";
import { useCart } from "./components/CartContext";
import "./Sidebar.css";
import ProfileSectionDropdown from "./components/ProfileSection";
import MobileProfile from "./components/MobileProfile";
import Popup from "./components/Popup";
import { ThemeContext } from "./context/themeContext";
import { saveUserHistory } from "./saveUserHistory";
import { useNavigate, useLocation } from "react-router-dom";
import { AvatarContext } from "./context/AvatarContext";
import { UserContext } from "./context/UserContext";

export default function Sidebar({ setToggle, toggle }) {
  const [showProfileSection, setShowProfileSection] = useState(false);
  const [showMobileProfile, setShowMobileProfile] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [soonPopup, setSoonPopup] = useState(null);
  const profileIconAnchorRef = useRef(null);
  const { cart } = useCart();
  const { activeTheme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const { avatar } = useContext(AvatarContext);
  const location = useLocation();
  const { user } = useContext(UserContext);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleHamburgerClick = () => setToggle(!toggle);

  const isActive = (path) => location.pathname.startsWith(path);
  const isExact = (path) => location.pathname === path;

  useEffect(() => {
    const mainContent = document.querySelector(".main-content");
    if (mainContent) {
      toggle
        ? mainContent.classList.add("shifted")
        : mainContent.classList.remove("shifted");
    }
  }, [toggle]);

  // useEffect(() => {
  //   const updateShortcutVisibility = () => {
  //     const width = window.innerWidth;
  //     setShowTabletChatbotShortcut(width >= 768 && width <= 1024);
  //   };
  //   updateShortcutVisibility();
  //   window.addEventListener("resize", updateShortcutVisibility);
  //   return () => window.removeEventListener("resize", updateShortcutVisibility);
  // }, []);

  const handleNavigation = async (path, state = {}) => {
    const fullUrl = `${process.env.REACT_APP_FRONTEND_URL}${path}`;
    const partA = crypto.randomUUID() + crypto.randomUUID();
    const partB = crypto.randomUUID() + crypto.randomUUID();
    await saveUserHistory(fullUrl, user?.user_id);
    navigate(`${path}/${partA}/${partB}`, { replace: true, state });
    setToggle(false);
  };

  return (
    <>
      {/* Mobile top header bar — hamburger left, logo+name centered */}
      <div className={`mobile-header-bar${activeTheme === "dark" ? " dark-mode" : ""}`}>
        <button onClick={handleHamburgerClick} className="mobile-header-btn">
          <PanelLeft size={20} />
        </button>
        <div className={`mobile-header-brand${(toggle || !isExact("/dashboard")) ? " mobile-header-brand--hidden" : ""}`}>
          <img src="/image/trans-logo.png" alt="MarsMate" className="mobile-header-logo" />
          <span className="mobile-header-name">MARSMATE</span>
        </div>
        <div className="mobile-header-spacer" />
      </div>

      {/* Mobile backdrop — closes sidebar when tapping outside */}
      {toggle && (
        <div
          className="sidebar-mobile-backdrop"
          onClick={() => setToggle(false)}
        />
      )}

      <div
        className={`sidebar ${toggle ? "open" : ""} ${
          activeTheme === "dark" ? "dark-mode" : ""
        }`}
      >
        {/* ── Brand name + Toggle button ── */}
        <div className="sidebar-top">
          <span className="sidebar-brand-name">MARSMATE</span>
          <button
            onClick={handleHamburgerClick}
            className="sidebar-toggle-btn"
          >
            <PanelLeft size={20} />
          </button>
        </div>

        {/* ── Navigation ── */}
        <nav className="sidebar-nav">
          {/* Home */}
          <div
            className={`sidebar-nav-item ${
              isExact("/dashboard") ? "active" : ""
            }`}
            onClick={() => {
              navigate("/dashboard");
              setToggle(false);
            }}
          >
            <div className="sidebar-nav-icon">
              <Home size={14} />
            </div>
            <span className="sidebar-nav-label">Home</span>
          </div>

          {/* Investor Matching — coming soon */}
          <div
            className="sidebar-nav-item"
            onClick={() => setSoonPopup({ title: "Investor Matching", icon: <Users size={17} />, color: "#C1440E" })}
          >
            <div className="sidebar-nav-icon">
              <Users size={14} />
              <span className="sidebar-soon-dot" title="Coming soon"><Clock size={9} /></span>
            </div>
            <span className="sidebar-nav-label">Investor Matching</span>
          </div>

          {/* Registration — coming soon */}
          <div
            className="sidebar-nav-item"
            onClick={() => setSoonPopup({ title: "Registration", icon: <UserPlus size={17} />, color: "#8B5CF6" })}
          >
            <div className="sidebar-nav-icon">
              <UserPlus size={14} />
              <span className="sidebar-soon-dot" title="Coming soon"><Clock size={9} /></span>
            </div>
            <span className="sidebar-nav-label">Registration</span>
          </div>

          {/* History section */}
          {toggle && (
            <>
              <div className="sidebar-section-divider" />
              <p className="sidebar-section-label">History</p>
            </>
          )}

          {/* Idea Validation Reports */}
          <div
            className={`sidebar-nav-item ${
              ((isActive("/reports-hub") || isActive("/reports/")) &&
                location.state?.tab !== "mm" &&
                location.state?.tab !== "mind-mapping") ||
              isActive("/report/") ||
              isActive("/report-b/") ||
              isActive("/scorecard/")
                ? "active"
                : ""
            }`}
            onClick={() => handleNavigation("/reports-hub", { tab: "idea" })}
          >
            <div className="sidebar-nav-icon">
              <FileText size={14} />
            </div>
            <span className="sidebar-nav-label">Idea Validation Reports</span>
          </div>

          {/* Mind Mapping Reports */}
          <div
            className={`sidebar-nav-item ${
              (isActive("/reports-hub") &&
                (location.state?.tab === "mm" ||
                  location.state?.tab === "mind-mapping")) ||
              isActive("/mind-mapping")
                ? "active"
                : ""
            }`}
            onClick={() => handleNavigation("/reports-hub", { tab: "mm" })}
          >
            <div className="sidebar-nav-icon">
              <Network size={14} />
            </div>
            <span className="sidebar-nav-label">Mind Mapping Reports</span>
          </div>

          {/* Plug & Play section */}
          {toggle && (
            <>
              <div className="sidebar-section-divider" />
              <p className="sidebar-section-label">Plug &amp; Play</p>
            </>
          )}

          {/* Products */}
          <div
            className={`sidebar-nav-item ${
              isActive("/product") ? "active" : ""
            }`}
            onClick={() => handleNavigation("/product")}
          >
            <div className="sidebar-nav-icon">
              <ShoppingBag size={14} />
            </div>
            <span className="sidebar-nav-label">Products</span>
          </div>

          {/* MM AI — tablet shortcut (commented out — not active)
          {showTabletChatbotShortcut && (
            <div
              className={`sidebar-nav-item ${
                isActive("/mm-ai") ? "active" : ""
              }`}
              onClick={() => handleNavigation("/mm-ai")}
            >
              <div className="sidebar-nav-icon">
                <img
                  src="/image/Chatbot-image.png"
                  alt="MM AI"
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              </div>
              <span className="sidebar-nav-label">MM AI</span>
            </div>
          )}
          */}

          {/* Cart */}
          <div
            className={`sidebar-nav-item ${
              isActive("/cart") ? "active" : ""
            }`}
            onClick={() => handleNavigation("/cart")}
          >
            <div className="sidebar-nav-icon sidebar-cart-wrap">
              <ShoppingCart size={14} />
              {cart.length > 0 && (
                <span className="cart-badge">{cart.length}</span>
              )}
            </div>
            <span className="sidebar-nav-label">Cart</span>
          </div>

          {/* About */}
          <div
            className={`sidebar-nav-item ${
              isActive("/landing") ? "active" : ""
            }`}
            onClick={() => {
              navigate("/landing", { state: { scrollTo: "about" } });
              setToggle(false);
            }}
          >
            <div className="sidebar-nav-icon">
              <Info size={14} />
            </div>
            <span className="sidebar-nav-label">About</span>
          </div>
        </nav>

        {/* ── Profile at bottom ── */}
        <div
          ref={profileIconAnchorRef}
          className="sidebar-bottom"
          onClick={() => {
            setToggle(false);
            if (isMobile) {
              setShowMobileProfile((p) => !p);
            } else {
              setShowProfileSection((p) => !p);
            }
          }}
          title="Profile"
        >
          <div 
            className="sidebar-profile-avatar"
            style={{
              background: (user?.profile_pic || avatar) ? "transparent" : (activeTheme === "dark" ? "#ffffff" : "#000000"),
              border: (user?.profile_pic || avatar) ? "none" : `2.5px solid ${activeTheme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)"}`
            }}
          >
            {(user?.profile_pic || avatar) ? (
              <img src={user?.profile_pic || avatar} alt="Profile" className="sidebar-profile-img" />
            ) : (
              <span
                className="sidebar-profile-initial"
                style={{ color: activeTheme === "dark" ? "#000000" : "#ffffff" }}
              >
                {user?.name?.trim()?.[0]?.toUpperCase() || "?"}
              </span>
            )}
          </div>
          <span className="sidebar-profile-name">{user?.name || "Account"}</span>
        </div>
      </div>

      {showProfileSection && !isMobile && (
        <ProfileSectionDropdown
          isOpen={showProfileSection}
          onClose={() => setShowProfileSection(false)}
          setToggle={setToggle}
          anchorRef={profileIconAnchorRef}
        />
      )}

      {showMobileProfile && isMobile && (
        <MobileProfile
          isOpen={showMobileProfile}
          onClose={() => setShowMobileProfile(false)}
        />
      )}

      <Popup
        show={!!soonPopup}
        onClose={() => setSoonPopup(null)}
        category={soonPopup}
      />

      {/* ── Collapsed sidebar brand — only on dashboard, hides when sidebar expands ── */}
      <div
        className={`sidebar-collapsed-brand${(toggle || !isExact("/dashboard")) ? " sidebar-collapsed-brand--hidden" : ""}${activeTheme === "dark" ? " dark" : ""}`}
      >
        <img
          src="/image/trans-logo.png"
          alt="MarsMate"
          className="sidebar-collapsed-icon"
        />
        <span className="sidebar-collapsed-brand-text">MARSMATE AI</span>
      </div>
    </>
  );
}
